import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { AdminTimesheetOverviewService } from '../../services/admin-timesheet-overview.service';
import {
  AdminWeeklyReportFilters,
  EmployeeWeekRowVm,
  WeekCellStatus,
  WeekTotals,
  WeeklyReportDto,
  WeeklyReportUserScheduleRow,
} from '../../models/admin-timesheet-overview.model';
import { FilterService } from '../../../services/filter.service';
import { BusyService } from '../../../services/busy.service';

type ReportCache = Map<string, WeeklyReportDto | null>;

type ReportEmployee = {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
};

interface AdminOverviewFiltersVm {
  companyIds: number[] | null;
  departmentIds: number[] | null;
  employeeId: number | null;
  searchTerm: string;
}

@Component({
  selector: 'app-admin-timesheet-overview',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './admin-timesheet-overview.component.html',
  styleUrls: ['./admin-timesheet-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminTimesheetOverviewComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly cache: ReportCache = new Map();
  private readonly searchInput$ = new Subject<string>();
  private readonly employeeIdInput$ = new Subject<number | null>();

  busyService = inject(BusyService);
  filterService = inject(FilterService);

  errorMessage: string | null = null;

  anchorWeek!: string;
  visibleWeeks: string[] = [];
  rows: EmployeeWeekRowVm[] = [];

  filters: AdminOverviewFiltersVm = {
    companyIds: null,
    departmentIds: null,
    employeeId: null,
    searchTerm: '',
  };

  constructor(
    private readonly svc: AdminTimesheetOverviewService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.bindSearch();
    this.bindEmployeeId();

    const urlWeek = this.route.snapshot.queryParamMap.get('week');
    this.anchorWeek = this.isValidYyyyWw(urlWeek) ? String(urlWeek) : this.getCurrentIsoYyyyWw();
    this.visibleWeeks = this.makeVisibleWeeks(this.anchorWeek);

    if (!this.isValidYyyyWw(urlWeek)) {
      this.syncWeekToUrl(true);
    }

    void this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchInput$.complete();
    this.employeeIdInput$.complete();
  }

  prev3(): void {
    this.anchorWeek = this.addWeeks(this.anchorWeek, -1);
    this.visibleWeeks = this.makeVisibleWeeks(this.anchorWeek);
    this.syncWeekToUrl();
    void this.loadReportsOnly();
  }

  next3(): void {
    this.anchorWeek = this.addWeeks(this.anchorWeek, 1);
    this.visibleWeeks = this.makeVisibleWeeks(this.anchorWeek);
    this.syncWeekToUrl();
    void this.loadReportsOnly();
  }

  openEmployee(employeeId: number, yyyyww: string): void {
    if (!employeeId || !this.isValidYyyyWw(yyyyww)) {
      return;
    }

    void this.router.navigate(['/timesheets/admin', employeeId, yyyyww], {
      queryParams: this.buildAdminReturnQueryParams(),
    });
  }

  private buildAdminReturnQueryParams(): {
    returnTo: 'overview';
    returnWeek: string;
    returnUrl: string;
  } {
    return {
      returnTo: 'overview',
      returnWeek: this.anchorWeek,
      returnUrl: this.router.url,
    };
  }

  get hasActiveFilters(): boolean {
    return (
      !!this.filters.companyIds?.length ||
      !!this.filters.departmentIds?.length ||
      this.filters.employeeId !== null ||
      !!this.filters.searchTerm.trim()
    );
  }

  onCompanyChange(value: number | string | null): void {
    const normalized = this.toNullableNumber(value);
    this.filters.companyIds = normalized !== null ? [normalized] : null;
    this.cache.clear();
    void this.load();
  }

  onDepartmentChange(value: number | string | null): void {
    const departmentId = this.toNullableNumber(value);
    this.filters.departmentIds = departmentId !== null ? [departmentId] : null;
    this.cache.clear();
    void this.load();
  }

  onEmployeeIdChange(value: number | string | null): void {
    const normalized = this.toNullableNumber(value);
    this.employeeIdInput$.next(normalized);
  }

  onSearchTermChange(value: string | null): void {
    const normalized = String(value ?? '').trimStart();
    this.searchInput$.next(normalized);
  }

  clearFilters(): void {
    this.filters = {
      companyIds: null,
      departmentIds: null,
      employeeId: null,
      searchTerm: '',
    };

    this.cache.clear();
    void this.load();
  }

  async load(): Promise<void> {
    await this.loadReportsOnly();
  }

  private bindSearch(): void {
    this.searchInput$.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((value) => {
      this.filters.searchTerm = value;
      this.cache.clear();
      void this.load();
    });
  }

  private bindEmployeeId(): void {
    this.employeeIdInput$.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((value) => {
      this.filters.employeeId = value;
      this.cache.clear();
      void this.load();
    });
  }

  private async loadReportsOnly(): Promise<void> {
    this.errorMessage = null;
    this.cd.detectChanges();

    try {
      const weeksToFetch = this.visibleWeeks.filter(
        (week) => !this.cache.has(this.getCacheKey(week)),
      );

      if (weeksToFetch.length) {
        const requests = weeksToFetch.map((week) =>
          this.svc.getWeeklyReportByFilters(this.buildWeeklyFilters(week)).pipe(
            map((response) => ({
              week,
              data: response.data,
            })),
            catchError(() => of({ week, data: null as WeeklyReportDto | null })),
          ),
        );

        const results = await firstValueFrom(forkJoin(requests));

        for (const result of results) {
          this.cache.set(this.getCacheKey(result.week), result.data ?? null);
        }
      }

      this.rows = this.buildRowsFromReportsOnly();
    } catch {
      this.errorMessage = 'Failed to load weekly reports';
      this.rows = [];
    } finally {
      this.cd.detectChanges();
    }
  }

  private buildWeeklyFilters(fullYearWeek: string): AdminWeeklyReportFilters {
    return {
      fullYearWeek,
      companyIds: this.filters.companyIds?.length ? this.filters.companyIds : null,
      departmentIds: this.filters.departmentIds?.length ? this.filters.departmentIds : null,
      employeeId: this.filters.employeeId,
      searchTerm: this.filters.searchTerm.trim() || null,
    };
  }

  private getCacheKey(week: string): string {
    const companyIds = this.filters.companyIds?.join(',') ?? '';
    const departmentIds = this.filters.departmentIds?.join(',') ?? '';
    const employeeId = this.filters.employeeId ?? '';
    const searchTerm = this.filters.searchTerm.trim().toLowerCase();

    return [
      week,
      `company:${companyIds}`,
      `department:${departmentIds}`,
      `employee:${employeeId}`,
      `search:${searchTerm}`,
    ].join('|');
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private buildRowsFromReportsOnly(): EmployeeWeekRowVm[] {
    const totalsByWeek = new Map<string, Map<number, WeekTotals>>();

    for (const week of this.visibleWeeks) {
      const report = this.cache.get(this.getCacheKey(week)) ?? null;
      totalsByWeek.set(week, this.computeTotalsForWeek(report));
    }

    const employees = this.getEmployeesFromVisibleReports();

    return employees.map((employee) => {
      const name =
        `${employee.lastName ?? ''}, ${employee.firstName ?? ''}`.replace(/^,\s*/, '').trim() ||
        `Employee ${employee.id}`;

      const weeks: Record<string, WeekTotals> = {};

      for (const week of this.visibleWeeks) {
        const totalsForWeek = totalsByWeek.get(week);

        weeks[week] = totalsForWeek?.get(employee.id) ?? {
          booked: 0,
          clocked: 0,
          scheduled: 0,
          status: 'none',
        };
      }

      return {
        employeeId: employee.id,
        name,
        weeks,
      };
    });
  }

  private getEmployeesFromVisibleReports(): ReportEmployee[] {
    const byId = new Map<number, ReportEmployee>();

    for (const week of this.visibleWeeks) {
      const report = this.cache.get(this.getCacheKey(week)) ?? null;

      if (!report) {
        continue;
      }

      for (const row of report.timeLogs ?? []) {
        const user = row.user;

        if (!user?.id || byId.has(user.id)) {
          continue;
        }

        byId.set(user.id, {
          id: user.id,
          firstName: user.firstName ?? null,
          lastName: user.lastName ?? null,
        });
      }

      for (const row of report.bookedhours ?? []) {
        const user = row.user;
        const id = user?.id;

        if (!id || byId.has(id)) {
          continue;
        }

        byId.set(id, {
          id,
          firstName: user.firstName ?? null,
          lastName: user.lastName ?? null,
        });
      }

      for (const row of report.userSchedules ?? []) {
        const user = row.user;
        const id = user?.id;

        if (!id || byId.has(id)) {
          continue;
        }

        byId.set(id, {
          id,
          firstName: user.firstName ?? null,
          lastName: user.lastName ?? null,
        });
      }
    }

    return Array.from(byId.values()).sort((a, b) => {
      const aName = `${a.lastName ?? ''} ${a.firstName ?? ''}`.trim().toLowerCase();
      const bName = `${b.lastName ?? ''} ${b.firstName ?? ''}`.trim().toLowerCase();

      return aName.localeCompare(bName);
    });
  }

  private computeTotalsForWeek(report: WeeklyReportDto | null): Map<number, WeekTotals> {
    const totals = new Map<number, WeekTotals>();

    if (!report) {
      return totals;
    }

    const payrollSynced = this.hasPayrollSync(report);

    const clockedByUser = new Map<number, number>();

    for (const row of report.timeLogs ?? []) {
      const id = row.user?.id;

      if (!id) {
        continue;
      }

      clockedByUser.set(id, this.computeClockedHoursForTimeLogRow(row));
    }

    const bookedByUser = new Map<number, number>();
    const allBookedApprovedByUser = new Map<number, boolean>();

    for (const row of report.bookedhours ?? []) {
      const id = row.user?.id;

      if (!id) {
        continue;
      }

      const bookedEntries = this.getBookedHourEntriesFromBookedRow(row);

      const bookedTotal = bookedEntries.reduce(
        (sum, bookedHour) => sum + (Number(bookedHour.hours) || 0),
        0,
      );

      bookedByUser.set(id, bookedTotal);

      allBookedApprovedByUser.set(
        id,
        bookedEntries.length > 0 &&
          bookedEntries.every((bookedHour) => this.isBookedHourSupervisorApproved(bookedHour)),
      );
    }

    const scheduledByUser = new Map<number, number>();

    for (const row of report.userSchedules ?? []) {
      const userId = row.user?.id;

      if (!userId) {
        continue;
      }

      scheduledByUser.set(userId, this.computeWeeklyScheduledHours(row));
    }

    const ids = new Set<number>([
      ...clockedByUser.keys(),
      ...bookedByUser.keys(),
      ...scheduledByUser.keys(),
    ]);

    for (const id of ids) {
      const booked = this.round2(bookedByUser.get(id) ?? 0);
      const clocked = this.round2(clockedByUser.get(id) ?? 0);
      const scheduled = this.round2(scheduledByUser.get(id) ?? 0);

      totals.set(id, {
        booked,
        clocked,
        scheduled,
        status: this.pickStatus({
          booked,
          clocked,
          payrollSynced,
          allBookedApproved: allBookedApprovedByUser.get(id) ?? false,
        }),
      });
    }

    return totals;
  }

  private computeClockedHoursForTimeLogRow(row: unknown): number {
    const typedRow = row as {
      totalHours?: number | string | null;
      logs?: Array<{
        timeLogs?: Array<{ totalHours?: number | string | null; hours?: number | string | null }> | null;
      }> | null;
      user?: {
        totalHours?: number | string | null;
        logs?: Array<{
          timeLogs?: Array<{ totalHours?: number | string | null; hours?: number | string | null }> | null;
        }> | null;
      } | null;
    };

    const directTotal = Number(typedRow.totalHours ?? typedRow.user?.totalHours);

    if (Number.isFinite(directTotal) && directTotal > 0) {
      return this.round2(directTotal);
    }

    const logs = [...(typedRow.logs ?? []), ...(typedRow.user?.logs ?? [])];

    return this.round2(
      logs.reduce((weekTotal, day) => {
        const dayTotal = (day.timeLogs ?? []).reduce(
          (sum, log) => sum + (Number(log.totalHours ?? log.hours) || 0),
          0,
        );

        return weekTotal + dayTotal;
      }, 0),
    );
  }

  private getBookedHourEntriesFromBookedRow(row: unknown): Array<{
    id?: number | null;
    hours?: number | string | null;
    supervisorApprovedDate?: string | null;
    approvedBySupervisor?: unknown | null;
    supervisorApprovedBy?: unknown | null;
  }> {
    const typedRow = row as {
      totalHours?: number | string | null;
      bookedHours?: Array<{
        bookedHours?: unknown[] | null;
        bookedHour?: unknown[] | null;
      }> | null;
      user?: {
        totalHours?: number | string | null;
        bookedHours?: Array<{
          bookedHours?: unknown[] | null;
          bookedHour?: unknown[] | null;
        }> | null;
      } | null;
    };

    const bookedDays = [...(typedRow.bookedHours ?? []), ...(typedRow.user?.bookedHours ?? [])];

    return bookedDays
      .flatMap((day) => day.bookedHours ?? day.bookedHour ?? [])
      .filter(
        (bookedHour): bookedHour is {
          id?: number | null;
          hours?: number | string | null;
          supervisorApprovedDate?: string | null;
          approvedBySupervisor?: unknown | null;
          supervisorApprovedBy?: unknown | null;
        } => Number((bookedHour as { hours?: number | string | null })?.hours) > 0,
      );
  }

  private computeWeeklyScheduledHours(row: WeeklyReportUserScheduleRow): number {
    const typedRow = row as WeeklyReportUserScheduleRow & {
      user?: {
        schedules?: WeeklyReportUserScheduleRow['schedules'];
        userAssignedSchedules?: WeeklyReportUserScheduleRow['schedules'];
      } | null;
      userAssignedSchedules?: WeeklyReportUserScheduleRow['schedules'];
    };

    const entries = [
      ...(typedRow.schedules ?? []),
      ...(typedRow.user?.schedules ?? []),
      ...(typedRow.userAssignedSchedules ?? []),
      ...(typedRow.user?.userAssignedSchedules ?? []),
    ];

    if (!entries.length) {
      return 0;
    }

    let total = 0;
    const seen = new Set<string>();

    for (const entry of entries) {
      const schedule = entry?.schedule;

      if (!entry?.assignedDate || !schedule?.localStartTime || !schedule?.localEndTime) {
        continue;
      }

      const key = `${entry.assignedDate}|${schedule.id}|${schedule.localStartTime}|${schedule.localEndTime}`;

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);

      total += this.computeDurationHours(
        schedule.localStartTime,
        schedule.localEndTime,
        Boolean(schedule.isOverNight),
        schedule.breakTime ?? null,
        schedule.breakThresholdHours ?? null,
      );
    }

    return this.round2(total);
  }

  private pickStatus(options: {
    booked: number;
    clocked: number;
    payrollSynced: boolean;
    allBookedApproved: boolean;
  }): WeekCellStatus {
    const bookedHours = this.round2(Number(options.booked ?? 0));
    const clockedHours = this.round2(Number(options.clocked ?? 0));

    if (bookedHours <= 0 && clockedHours <= 0) {
      return this.toWeekCellStatus('none');
    }

    if (options.payrollSynced && bookedHours > 0 && options.allBookedApproved) {
      return this.toWeekCellStatus('approved');
    }

    if (this.isMatch(bookedHours, clockedHours) && bookedHours > 0) {
      return this.toWeekCellStatus('match');
    }

    if (clockedHours - bookedHours > 0.01) {
      return this.toWeekCellStatus('over-booked');
    }

    if (bookedHours - clockedHours > 0.01) {
      return this.toWeekCellStatus('under-booked');
    }

    return this.toWeekCellStatus('none');
  }

  private hasPayrollSync(report: WeeklyReportDto): boolean {
    const candidate = report as WeeklyReportDto & {
      payableHoursSync?: { id?: number | string | null } | null;
      payableHoursSyncs?: Array<{ id?: number | string | null }> | null;
      data?: {
        payableHoursSync?: { id?: number | string | null } | null;
        payableHoursSyncs?: Array<{ id?: number | string | null }> | null;
      } | null;
    };

    const syncCandidates = [
      candidate.payableHoursSync,
      candidate.data?.payableHoursSync,
      ...(candidate.payableHoursSyncs ?? []),
      ...(candidate.data?.payableHoursSyncs ?? []),
    ];

    return syncCandidates.some((sync) => {
      const id = Number(sync?.id);

      return Number.isFinite(id) && id > 0;
    });
  }

  private isBookedHourSupervisorApproved(bookedHour: unknown): boolean {
    const item = bookedHour as {
      supervisorApprovedDate?: string | null;
      approvedBySupervisor?: unknown | null;
      supervisorApprovedBy?: unknown | null;
    };

    return !!item.supervisorApprovedDate || !!item.approvedBySupervisor || !!item.supervisorApprovedBy;
  }

  private round2(value: number): number {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
  }

  private toWeekCellStatus(status: string): WeekCellStatus {
    return status as WeekCellStatus;
  }

  cellClass(totals: WeekTotals | null | undefined): string {
    return totals?.status ?? 'none';
  }

  private syncWeekToUrl(replaceUrl = false): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        week: this.anchorWeek,
      },
      queryParamsHandling: 'merge',
      replaceUrl,
    });
  }

  private isValidYyyyWw(value: string | null | undefined): value is string {
    if (!value || !/^\d{6}$/.test(String(value).trim())) {
      return false;
    }

    const year = Number(String(value).slice(0, 4));
    const week = Number(String(value).slice(4, 6));

    return Number.isFinite(year) && Number.isFinite(week) && week >= 1 && week <= 53;
  }

  private makeVisibleWeeks(anchor: string): string[] {
    return [this.addWeeks(anchor, -2), this.addWeeks(anchor, -1), anchor];
  }

  private getCurrentIsoYyyyWw(): string {
    return this.dateToIsoWeek(new Date());
  }

  private addWeeks(yyyyww: string, deltaWeeks: number): string {
    const monday = this.isoWeekToMonday(yyyyww);
    monday.setUTCDate(monday.getUTCDate() + deltaWeeks * 7);

    return this.dateToIsoWeek(monday);
  }

  private isoWeekToMonday(yyyyww: string): Date {
    const { year, week } = this.parseYyyyWw(yyyyww);

    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Day = jan4.getUTCDay() || 7;

    const week1Monday = new Date(jan4);
    week1Monday.setUTCDate(jan4.getUTCDate() - (jan4Day - 1));

    const monday = new Date(week1Monday);
    monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);

    return monday;
  }

  private dateToIsoWeek(date: Date): string {
    const utcDate = new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
    );

    const day = utcDate.getUTCDay() || 7;
    utcDate.setUTCDate(utcDate.getUTCDate() + (4 - day));

    const year = utcDate.getUTCFullYear();

    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Day = jan4.getUTCDay() || 7;

    const week1Thursday = new Date(jan4);
    week1Thursday.setUTCDate(jan4.getUTCDate() + (4 - jan4Day));

    const diffDays = Math.round((utcDate.getTime() - week1Thursday.getTime()) / 86400000);
    const week = 1 + Math.floor(diffDays / 7);

    return `${year}${String(week).padStart(2, '0')}`;
  }

  private parseYyyyWw(yyyyww: string): { year: number; week: number } {
    const value = String(yyyyww).trim();

    if (!/^\d{6}$/.test(value)) {
      throw new Error(`Invalid YYYYWW: ${yyyyww}`);
    }

    const year = Number(value.slice(0, 4));
    const week = Number(value.slice(4, 6));

    if (!Number.isFinite(year) || !Number.isFinite(week) || week < 1 || week > 53) {
      throw new Error(`Invalid ISO week: ${yyyyww}`);
    }

    return { year, week };
  }

  private computeDurationHours(
    start: string,
    end: string,
    isOverNight = false,
    breakTime?: string | null,
    breakThresholdHours?: number | null,
  ): number {
    const [startHour, startMinute] = start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);

    if (
      !Number.isFinite(startHour) ||
      !Number.isFinite(startMinute) ||
      !Number.isFinite(endHour) ||
      !Number.isFinite(endMinute)
    ) {
      return 0;
    }

    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;

    let diffMinutes = endTotalMinutes - startTotalMinutes;

    if (isOverNight || diffMinutes < 0) {
      diffMinutes += 1440;
    }

    const threshold = Number(breakThresholdHours ?? 0);
    const shouldSubtractBreak = threshold <= 0 || diffMinutes >= threshold * 60;

    if (shouldSubtractBreak) {
      diffMinutes = Math.max(0, diffMinutes - this.parseBreakMinutes(breakTime));
    }

    return this.round2(diffMinutes / 60);
  }

  private parseBreakMinutes(value: string | null | undefined): number {
    if (!value) {
      return 0;
    }

    const [hoursRaw, minutesRaw] = String(value).split(':');
    let hours = Number(hoursRaw);
    const minutes = Number(minutesRaw);

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return 0;
    }

    if (hours === 12) {
      hours = 0;
    }

    return Math.max(0, hours * 60 + minutes);
  }

  private isMatch(a: number, b: number, tolerance = 0.01): boolean {
    return Math.abs(Number(a ?? 0) - Number(b ?? 0)) <= tolerance;
  }

  trackByEmployeeId(_: number, row: EmployeeWeekRowVm): number {
    return row.employeeId;
  }

  get selectedCompanyId(): number | null {
    return this.filters.companyIds?.length ? this.filters.companyIds[0] : null;
  }

  set selectedCompanyId(value: number | null) {
    this.filters.companyIds = value !== null ? [value] : null;
  }

  get selectedDepartmentId(): number | null {
    return this.filters.departmentIds?.length ? this.filters.departmentIds[0] : null;
  }

  set selectedDepartmentId(value: number | null) {
    this.filters.departmentIds = value !== null ? [value] : null;
  }
}